import { pcfRoutes } from '@app/pcf/pcf-routes';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@shared/components/ui/collapsible';
import { Separator } from '@shared/components/ui/separator';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@shared/components/ui/sidebar';
import { useIsMobile } from '@shared/hooks/use-mobile';
import { cn } from '@shared/lib/utils';
import { ChevronRight } from 'lucide-react';
import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MainNavItem, NavItem } from './app-sidebar';

function hasSubItems(item: MainNavItem) {
  return item.items?.some((subItem) => subItem.show);
}

function isPathActive(targetPath: string, currentPath: string, exact = false) {
  if (!targetPath) return false;
  // Links may carry search params (e.g. the group dashboard's global
  // filters) — active state is a path concern, so match on the path only.
  const target = targetPath.split('?')[0];
  if (target === currentPath) return true;
  if (exact) return false;
  if (target === '/') return false;
  return currentPath.startsWith(`${target}/`);
}

export function NavMain({ items }: { items: MainNavItem[] }) {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const dashboardUrls = ['/', pcfRoutes.PRODUCT_DASHBOARD];
  function isDashboardActive(item: NavItem) {
    return item.to === dashboardUrls[0] && pathname === dashboardUrls[1];
  }

  function subItemIsActive(item: MainNavItem) {
    return item.items?.some((subItem) => {
      return (
        isDashboardActive(subItem) ||
        isPathActive(subItem.to, pathname, subItem.exact)
      );
    });
  }

  function isSubItemActive(subItem: NavItem, siblings: NavItem[] = []) {
    const matches =
      isDashboardActive(subItem) ||
      isPathActive(subItem.to, pathname, subItem.exact);

    if (!matches) return false;

    return !siblings.some(
      (sibling) =>
        sibling !== subItem &&
        sibling.show &&
        sibling.to.length > subItem.to.length &&
        (isDashboardActive(sibling) ||
          isPathActive(sibling.to, pathname, sibling.exact))
    );
  }

  function subMenuButtonTooltip({ item }: { item: MainNavItem }) {
    return (
      <div className="bg-gray-00">
        <Text variant="subtle" className="px-2 py-1.5 pb-2">
          {item.title}
        </Text>
        <Separator />
        <div className="mt-2 flex flex-col gap-1">
          {item.items?.map(
            (subItem) =>
              subItem.show && (
                <Link
                  to={subItem.to}
                  key={subItem.title}
                  className={cn(
                    'rounded-md px-3 py-2 hover:bg-sidebar-accent',
                    {
                      'bg-sidebar-accent': isSubItemActive(subItem, item.items),
                    }
                  )}
                >
                  {subItem.title}
                </Link>
              )
          )}
        </div>
      </div>
    );
  }

  return (
    <SidebarGroup className={cn('pt-5', {})}>
      <SidebarMenu>
        {items.map(
          (item) =>
            item.show && (
              <Collapsible
                key={item.title}
                asChild
                defaultOpen={item.isActive}
                className="group/collapsible border-l-0 font-medium text-primary"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      collapsedTooltip={
                        hasSubItems(item) &&
                        !item.disabled &&
                        state === 'collapsed' &&
                        (isMobile || state === 'collapsed')
                          ? {
                              children: subMenuButtonTooltip({ item }),
                            }
                          : undefined
                      }
                      tooltip={item.tooltip && item.tooltip}
                      className={cn('mb-1 border-l-0 py-5 px-3', {
                        'data-[state=closed]:bg-sidebar-accent':
                          subItemIsActive(item),
                      })}
                      isActive={
                        ((subItemIsActive(item) ||
                          isPathActive(item.to, pathname, item.exact)) &&
                          state === 'collapsed') ||
                        (!hasSubItems(item) &&
                          isPathActive(item.to, pathname, item.exact))
                      }
                      onClick={() => {
                        if (!hasSubItems(item)) {
                          navigate(item.to);
                        }
                      }}
                      disabled={item.disabled}
                    >
                      {item.icon}
                      <span className="ml-1">{item.title}</span>
                      {item.items && !item.disabled && (
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      )}
                      {item.disabled && (
                        <Icons.lock className="ml-auto text-gray-500" />
                      )}
                      {item.badge && (
                        <div className="ml-auto">{item.badge}</div>
                      )}
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    {item.items?.map((subItem) => {
                      if (!subItem.show) return null;
                      const subButton = (
                        <SidebarMenuSubButton
                          asChild
                          isActive={isSubItemActive(subItem, item.items)}
                          className="py-5 pl-4"
                          aria-disabled={subItem.disabled}
                          onClick={(e) => {
                            if (subItem.disabled) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                          }}
                        >
                          <Link
                            to={subItem.to}
                            tabIndex={subItem.disabled ? -1 : 0}
                            data-testid={subItem.testId}
                          >
                            {subItem.icon}
                            <span>{subItem.title}</span>
                            {subItem.disabled && (
                              <Icons.lock className="ml-auto text-gray-500" />
                            )}
                            {subItem.badge}
                          </Link>
                        </SidebarMenuSubButton>
                      );
                      return (
                        <SidebarMenuSub
                          className="mr-0 border-l-0 pr-0"
                          key={subItem.title}
                        >
                          <SidebarMenuSubItem>
                            {subItem.tooltip ? (
                              <SimpleTooltip
                                side="right"
                                className="max-w-md"
                                trigger={
                                  <span className="block">{subButton}</span>
                                }
                                content={subItem.tooltip as ReactNode}
                              />
                            ) : (
                              subButton
                            )}
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      );
                    })}
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
        )}
      </SidebarMenu>
    </SidebarGroup>
  );
}
